// The rules of a webtop record, shared by the server (server/lib/store.js, which stores the remote webtops) and the
// page (applets/storage/src/local.js, which stores the local ones in the browser): what an empty webtop is, what a tile of each
// kind may hold (newShortcut, the only place shortcut fields are validated and length-capped), groups as a tree
// (tidyGroups), what a deletion takes along (removeTiles), importing and moving tiles, and the migrations of old
// tiles. Like chart-data.js it is imported on both sides, so it must stay free of DOM and Node APIs.
import { RENAMED_RANGES } from './chart-data.js';
import { COMMAND_TARGET_RE, ID_RE as APPLET_ID_RE, NAME_RE as APPLET_NAME_RE, SIZES_ALL } from './applet-model.js';

// A v4 UUID: crypto.randomUUID where there is one (Node, and a page on HTTPS or localhost), else built from random
// bytes (on the plain-HTTP LAN address browsers hide randomUUID).
export const uuid = () => globalThis.crypto.randomUUID?.() ?? '10000000-1000-4000-8000-100000000000'
  .replace(/[018]/g, (c) => (c ^ (globalThis.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));

// Auto Arrange's Sort By (settings.sortBy): manual (the order tiles are dropped in), or a sort the page applies
// (client/lib/arrange.js, SORTS).
export const SORT_BY = ['manual', 'name', 'type', 'size', 'added'];

export function emptyWebtop(slug) {
  return {
    slug,
    title: slug === 'root' ? 'webtop' : slug,
    background: null,
    // snap: tiles land on the grid (the Grid layout, the default; off is Freeform); autoArrange: tiles are packed from
    // the top left in order (the Auto Arrange layout), sortBy (one of SORT_BY) when it is on. Webtops from before
    // 2026-10-28 keep the snap: false they have saved (Freeform). viewport: each screen size keeps its own spots and
    // sizes (the Viewport layout, a tile's `layouts`), with snap saying whether drops land on the grid.
    settings: { snap: true, autoArrange: false, sortBy: 'manual', viewport: true },
    // Who may view it: 'public' (listed, anyone) | 'linked' (anyone with the URL; not listed) | 'private' (only the
    // owner). Only the owner edits it whatever the visibility, the owner being whoever unlocks it (its password or its
    // signed-in owner), or anyone on a webtop with neither (auth.canEdit).
    visibility: 'public',
    owner: null, // the signed-in user who owns it (session.ownerId: SHA-256 of their email), who may always edit it
    // Where it is stored: 'remote' (the webtop server, the default) or the `value` of a client storage in the browser
    // (applets/storage/src/local.js, CLIENT_STORAGES: 'local', IndexedDB; 'files', the Origin Private File System), whose webtops have a
    // `_` before their slug. The page moves a webtop between them (applets/storage/src/moves.js); each store only ever holds its own.
    storage: 'remote',
    shortcuts: [],
    // What applets keep on this webtop (storage with {scope: 'webtop'}, docs/applets.md): {<applet id>: {<key>: value}},
    // written by its editors through PATCH `applets`, APPLET_DATA_MAX in all.
    applets: {},
    updatedAt: null,
  };
}
export const VISIBILITIES = ['public', 'linked', 'private'];
// A webtop's slug on the server (its address, its file's or document's key). The page's own rule (client/lib/state.js) also
// allows a `_` in front: a webtop stored in the browser, which the server never holds.
export const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,39}$/;
// A webtop's address on the page: `/` for the root, `/@<slug>` for any other, so every other path stays free for the
// app. The one place that says so: everything that writes or reads an address goes through these two.
export const webtopHref = (slug) => (slug === 'root' ? '/' : `/@${slug}`);
// A slug in an address: the server's rule, or that with a `_` in front for a webtop stored in the browser.
export const PAGE_SLUG_RE = /^_?[a-z0-9][a-z0-9-]{0,39}$/;
// The slug an address's path names, '' for any path that is no webtop's (the page then shows there is none here).
export function slugOfPath(path) {
  if (path === '/') return 'root';
  return path.startsWith('/@') && PAGE_SLUG_RE.test(path.slice(2)) ? path.slice(2) : '';
}

// What a list of webtops shows of one (the server's GET /api/webtops, a client storage's list(), applets/storage/src/local.js): its
// address, title, number of tiles, colour (its own, under an image too, or null for the stylesheet's) and last change.
export const summary = (d) => ({
  slug: d.slug, title: d.title, count: d.shortcuts?.length ?? 0, updatedAt: d.updatedAt ?? null,
  color: (d.background?.type === 'color' ? d.background.value : d.background?.color) ?? null,
});
// Linked and private webtops need something to unlock them with, or nobody could edit (or see) them; a webtop that
// loses its last one (password removed, ownership given up) becomes public, as nothing protects it any more.
export const unprotected = (d) => !d.password && !d.owner;

// A chart's stored range under its current name (unchanged when the preset has not renamed it).
function renamedRange(preset, range) {
  const renamed = Object.hasOwn(RENAMED_RANGES, preset ?? '') ? RENAMED_RANGES[preset] : null;
  return renamed && Object.hasOwn(renamed, range ?? '') ? renamed[range] : range;
}

// Tiles written by older versions: the old name of the two-wide size, then any size a kind no longer offers (notes
// could be 1x1 before 2026-10-24, charts 2x2 or 3x4 before 2026-10-25) becomes its default; weather charts' '-15' and
// '-7' ranges became '-14' on 2026-10-25 (RENAMED_RANGES); notes kept their text in a field of their own before
// widgets had `data`; a weather, Spotify or chart widget is an applet's since 2026-10-31 (APPLET_KINDS). The record's own old
// fields are the server's (store.migrate).
export function migrateTiles(d) {
  for (const s of d.shortcuts ?? []) {
    s.added ??= 0; // added before 2026-10-28, when tiles started keeping the date (newShortcut)
    if (Object.hasOwn(APPLET_KINDS, s.kind)) Object.assign(s, appletTile(s));
    if (SIZES[s.kind] && !isSize(s.size)) s.size = SIZES[s.kind][0];
    if (s.layouts) { s.layouts = cleanLayouts(s.layouts, s.kind); if (!s.layouts) delete s.layouts; }
    if (s.kind === 'applet' && s.data?.applet === APPLET_KINDS.chart.applet && s.data.range !== undefined) s.data.range = renamedRange(s.data.preset, s.data.range);
    if (s.kind !== 'note' || !('text' in s)) continue;
    s.data = { ...(isData(s.data) ? s.data : {}), text: s.data?.text ?? s.text };
    delete s.text;
  }
  return d;
}

// The webtop's own fields a PATCH may change, besides its visibility and background (each side handles those: the
// server tidies up an uploaded image, a local webtop keeps its image inside the record): the title and the
// settings, each only when given and valid, and `applets`: {<applet id>: {<key>: value} | null}, merged into what
// that applet keeps on the webtop (a null value drops the key, null for the applet drops them all), refused when the
// whole would pass APPLET_DATA_MAX (throws an Error with `status` 413).
export const APPLET_DATA_MAX = 32 * 1024;
export function applyFields(d, body) {
  if (typeof body.title === 'string') d.title = body.title.slice(0, 80);
  const settings = body.settings;
  if (settings && typeof settings === 'object') {
    if (typeof settings.snap === 'boolean') d.settings.snap = settings.snap;
    if (typeof settings.autoArrange === 'boolean') d.settings.autoArrange = settings.autoArrange;
    if (typeof settings.viewport === 'boolean') d.settings.viewport = settings.viewport;
    if (SORT_BY.includes(settings.sortBy)) d.settings.sortBy = settings.sortBy;
  }
  if (isData(body.applets)) {
    const next = { ...(isData(d.applets) ? d.applets : {}) };
    for (const [id, patch] of Object.entries(body.applets)) {
      if (!APPLET_ID_RE.test(id)) continue;
      if (patch === null) { delete next[id]; continue; }
      if (!isData(patch)) continue;
      const values = { ...(isData(next[id]) ? next[id] : {}) };
      for (const [k, v] of Object.entries(patch)) {
        if (k.length > 200) continue;
        if (v === null || v === undefined) delete values[k]; else values[k] = v;
      }
      if (Object.keys(values).length) next[id] = values; else delete next[id];
    }
    if (JSON.stringify(next).length > APPLET_DATA_MAX) throw Object.assign(new Error(`applets keep at most ${APPLET_DATA_MAX} bytes on a webtop`), { status: 413, expose: true });
    d.applets = next;
  }
  return d;
}

// A tile's x or y as stored: a whole number of CSS px (unscaled), from 0 to POS_MAX (far past any screen; the layout
// brings a tile back on to it, and nothing unbounded or infinite reaches the record). Missing, empty or not a number
// is `fallback`.
const POS_MAX = 100_000;
export function coord(v, fallback) {
  const n = v == null || v === '' ? NaN : Number(v);
  return Number.isFinite(n) ? Math.min(Math.max(Math.round(n), 0), POS_MAX) : fallback;
}

// The screen sizes that keep a spot and size of their own under the Viewport layout (a tile's `layouts`). The large
// screen has none: it is the tile's own x, y and size, so every other layout, an old export and the assistant read it.
export const LAYOUT_BANDS = ['s', 'm', 'w'];
// A tile's `layouts` as stored: {s: {x, y, size}, m: {x, y, size}}, each part only when set (a size that is no whole number
// of cells is dropped, so the tile's own applies). Answers undefined when nothing is left, so the field stays off the record.
export function cleanLayouts(layouts, kind) {
  const out = {};
  for (const band of LAYOUT_BANDS) {
    const given = layouts?.[band];
    if (!given || typeof given !== 'object') continue;
    const one = {};
    if (Number.isFinite(given.x) && Number.isFinite(given.y)) Object.assign(one, { x: coord(given.x, 0), y: coord(given.y, 0) });
    if (SIZES[kind] && isSize(given.size)) one.size = given.size;
    if (Object.keys(one).length) out[band] = one;
  }
  return Object.keys(out).length ? out : undefined;
}

// Many tiles moved at once: `updates` is [{id, x, y, band?}, ...], with `band` the spot of that screen size instead of
// the tile's own, or [{id, clear: [band, ...]}, ...] to forget what those screen sizes saved of it; a tile not found, or
// a coordinate that is not a number, is left as it is.
export function moveTiles(shortcuts, updates) {
  for (const u of updates) {
    const s = shortcuts.find((x) => x.id === u?.id);
    if (!s) continue;
    if (Array.isArray(u.clear)) {
      const left = { ...s.layouts };
      for (const band of u.clear) if (LAYOUT_BANDS.includes(band)) delete left[band];
      s.layouts = cleanLayouts(left, s.kind);
      if (!s.layouts) delete s.layouts;
      continue;
    }
    if (LAYOUT_BANDS.includes(u.band)) {
      if (Number.isFinite(u.x) && Number.isFinite(u.y)) s.layouts = cleanLayouts({ ...s.layouts, [u.band]: { ...s.layouts?.[u.band], x: u.x, y: u.y } }, s.kind);
      continue;
    }
    if (Number.isFinite(u.x)) s.x = coord(u.x, s.x);
    if (Number.isFinite(u.y)) s.y = coord(u.y, s.y);
  }
  return shortcuts;
}

export const IMPORT_MAX = 500; // tiles one import may bring
export const TILES_MAX = 2000; // tiles a webtop holds at most: a request that would pass it is refused (the routes, applets/storage/src/local.js)
// Tiles imported from an exported webtop (or a bookmarks file the page has read, or the page's own copies for
// Duplicate), each through newShortcut, added to `shortcuts` (replacing them all with `replace`). Imported tiles
// get new ids, so a group's references to its members are moved over to the new ones. Answers the new tiles' ids in
// the order they were sent.
export function importTiles(shortcuts, incoming, replace = false) {
  const ids = new Map();
  const added = incoming.map((raw) => {
    const s = newShortcut(raw ?? {});
    if (raw?.id) ids.set(String(raw.id), s.id);
    return s;
  });
  for (const s of added) if (s.kind === 'group') s.data.items = s.data.items.map((id) => ids.get(id)).filter(Boolean);
  if (replace) shortcuts.length = 0;
  shortcuts.push(...added);
  return added.map((s) => s.id);
}

// A tile's kind: a shortcut ('url', 'webtop', or 'command': runs an installed applet's command, its target
// `<applet id>/<command>`, or launches the applet, its target the id alone; docs/applets.md), a group of shortcuts ('group') or a widget ('note',
// 'clock', 'countdown', 'slideshow', or 'applet', a widget an applet provides: data.applet and
// data.widget name it, docs/applets.md). Widgets carry their own fields.
export const KINDS = ['url', 'webtop', 'command', 'group', 'note', 'clock', 'countdown', 'slideshow', 'applet'];
// Built-in kinds that became applets' widgets: a tile of one of these kinds (an older webtop, an export, the
// assistant naming it) is that applet's widget, kind 'applet', its data kept for the widget. The Weather applet is
// part of webtop (applets/weather); the Spotify one is installed like any applet (applets/spotify), so its tiles ask
// for it until it is.
export const APPLET_KINDS = {
  weather: { applet: 'dev.webtop.weather', widget: 'weather' },
  spotify: { applet: 'dev.webtop.spotify', widget: 'player' },
  chart: { applet: 'dev.webtop.charts', widget: 'chart' },
  rss: { applet: 'dev.webtop.rss', widget: 'feed', title: 'RSS Feed' }, // `title`: the old kind's default title
  iframe: { applet: 'dev.webtop.webpage', widget: 'page', title: 'Web Page' },
};
// The applet tile an old kind's tile becomes. A title that was only the old kind's default (an RSS card's "RSS Feed",
// shown under the feed's own title then) is dropped, so the widget's own title shows, as it did.
const appletTile = (s) => {
  const { applet, widget, title } = APPLET_KINDS[s.kind];
  return {
    kind: 'applet', data: { ...(isData(s.data) ? s.data : {}), applet, widget },
    ...(title !== undefined && s.title === title ? { title: '' } : {}),
  };
};
// The sizes each kind offers, in grid cells written width x height (3x1 is three cells wide and one tall); the first is
// the default. A tile may also hold any other whole number of cells (isSize: dragging a corner), on any kind.
const SIZE_RE = /^([1-9]|1[0-2])x([1-9]|1[0-2])$/;
export const isSize = (v) => typeof v === 'string' && SIZE_RE.test(v);
export const SIZES = {
  url: ['1x1', '2x2'], webtop: ['1x1', '2x2'], command: ['1x1', '2x2'], group: ['1x1', '2x1', '3x1'], note: ['2x2', '2x1', '3x1', '3x2'],
  clock: ['2x1', '2x2', '3x2'],
  countdown: ['2x1', '3x1', '2x2', '3x2'],
  slideshow: ['2x2', '1x1', '2x1', '3x1', '3x3'],
  applet: SIZES_ALL, // the widget's own list is the applet's (widgets/applet.js); the record allows any
};
const HEX = /^#[0-9a-f]{6}$/i;
// `data` is a tile's own state (a note's text, a widget's settings), read and written through the
// /shortcuts/:id/data routes so a new widget needs no new fields here: a plain JSON object of at most DATA_MAX
// bytes. Kind-specific rules on it (like a note's text cap) still live in newShortcut.
export const DATA_MAX = 16 * 1024;
export const isData = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
export const dataFits = (v) => JSON.stringify(v).length <= DATA_MAX;
// ---- assets: the files a webtop's owner uploads (or has the server fetch by address), kept apart from the record by
// the server (server/lib/uploads.js) or, for a webtop stored in the browser, by its client storage (applets/storage/src/local.js), and
// named <slug>_<key>.<ext>. A record refers to one by its address, /assets/<name> (served by server/routes/uploads.js;
// applets/storage/src/assets.js shows a `_` slug's from the browser), in whatever field shows it: the background, a slideshow's
// images, a widget's data later. So what a webtop's own assets are is read off the record as a whole (assetNames),
// and a store deletes the ones nothing refers to any more after each save. Images are the only kind so far: a new
// kind is a row in ASSET_TYPES (and its signature in looksLike, server/lib/uploads.js), nothing else.
export const ASSET_TYPES = {
  'image/png': { ext: 'png', kind: 'image' },
  'image/jpeg': { ext: 'jpg', kind: 'image' },
  'image/gif': { ext: 'gif', kind: 'image' },
  'image/webp': { ext: 'webp', kind: 'image' },
  'image/svg+xml': { ext: 'svg', kind: 'image' },
  'image/avif': { ext: 'avif', kind: 'image' },
};
export const ASSET_LIMIT = 12 * 1024 * 1024; // the most bytes one asset may be, uploaded or fetched
const EXT_TYPE = Object.fromEntries(Object.entries(ASSET_TYPES).map(([type, { ext }]) => [ext, type]));
/** The extension an asset of `type` is named with, or null for a type that is not taken. */
export const assetExt = (type) => ASSET_TYPES[type]?.ext ?? null;
/** The content type an asset's name says it has, or null for an extension that is not one of ASSET_TYPES. */
export const assetType = (name) => EXT_TYPE[String(name).split('.').pop()] ?? null;
/** A name for a new asset of `type` of webtop `slug`: <slug>_<key>.<ext>, the key eight random bytes in hex. */
export function newAssetName(slug, type) {
  const key = Array.from(globalThis.crypto.getRandomValues(new Uint8Array(8)), (b) => b.toString(16).padStart(2, '0')).join('');
  return `${slug}_${key}.${assetExt(type) ?? 'bin'}`;
}
// An asset's address: its name (the owner's slug, `_` and a key, then the extension) after /assets/. The name is a
// file name: nothing in it can leave a folder.
const REF = '\\/assets\\/((_?[a-z0-9][a-z0-9-]{0,39})_[a-z0-9]{1,24}\\.[a-z0-9]{2,5})';
const ASSET_REF = new RegExp(`^${REF}$`);
const ASSET_REFS = new RegExp(REF, 'g');
export const ASSET_NAME_RE = /^_?[a-z0-9][a-z0-9-]{0,39}_[a-z0-9]{1,24}\.[a-z0-9]{2,5}$/;
/** Whether `v` is an asset's address and nothing else. */
export const isAssetRef = (v) => typeof v === 'string' && ASSET_REF.test(v);
/** The store name an asset's address refers to (`<slug>_<key>.<ext>`), or null. */
export const assetName = (ref) => (typeof ref === 'string' && ASSET_REF.exec(ref)?.[1]) || null;
/** The slug of the webtop an asset's address belongs to, or null. */
export const assetOwner = (ref) => (typeof ref === 'string' && ASSET_REF.exec(ref)?.[2]) || null;
/** Every asset address in `value` (a record, a tile, a list of tiles), each once: [{ref, name, owner}]. */
export function assetRefs(value) {
  const seen = new Map();
  for (const m of JSON.stringify(value ?? null).matchAll(ASSET_REFS)) if (!seen.has(m[0])) seen.set(m[0], { ref: m[0], name: m[1], owner: m[2] });
  return [...seen.values()];
}
/** The names of webtop `d`'s own assets that its record refers to; another webtop's (a tile copied from there) are its. */
export function assetNames(d) {
  return new Set(assetRefs(d).filter((r) => r.owner === d.slug).map((r) => r.name));
}
/**
 * `value` (a record, a tile) as a new object with every asset address replaced by `to(name, owner)`: a new address,
 * or null for '' so the field's own rule drops it (a slideshow keeps only valid images, newShortcut). Used when tiles
 * are copied to another webtop (the server's import, applets/storage/src/local.js) and when a webtop moves between storages
 * (applets/storage/src/moves.js).
 */
export function rewriteAssets(value, to) {
  return JSON.parse(JSON.stringify(value).replace(ASSET_REFS, (ref, name, owner) => to(name, owner) ?? ''));
}
/**
 * A PATCH's background: null (the client's own), a colour, or an image with its colour and blur, the image being one of
 * this webtop's own assets (the page sends the record's value back with another colour or blur) or an http(s)
 * address. Only those keys are kept. Anything else is undefined: refused. Another webtop's asset is not this one's
 * to show (nor to delete when it is replaced).
 */
export function cleanBackground(b, slug) {
  if (b === null) return null;
  if (!isData(b)) return undefined;
  if (b.type === 'color') return HEX.test(b.value ?? '') ? { type: 'color', value: b.value.toLowerCase() } : undefined;
  if (b.type !== 'image') return undefined;
  const value = typeof b.value === 'string' ? b.value.trim().slice(0, 2000) : '';
  if (assetOwner(value) !== slug && !/^https?:\/\/\S+$/i.test(value)) return undefined;
  if (b.color != null && !HEX.test(b.color)) return undefined;
  return { type: 'image', value, color: b.color == null ? null : b.color.toLowerCase(), blur: b.blur === true };
}

// A slideshow's images (data.images, in the order shown): each the address of one of a webtop's assets or an http(s)
// address, at most SLIDES_MAX of them.
export const SLIDES_MAX = 50;
export const SLIDE_INTERVALS = [3, 5, 10, 30, 60]; // seconds each image is shown (data.interval)
const SLIDE_EFFECTS = ['fade', 'slide'];
const SLIDE_FITS = ['cover', 'contain'];
const slideImage = (v) => typeof v === 'string' && v.length <= 2000 && (isAssetRef(v) || /^https?:\/\/\S+$/i.test(v));
const GROUP_MAX = 200; // tiles one group can hold
const GROUPABLE = ['url', 'webtop', 'command', 'group'];

// Keeps the groups of a webtop a tree, whatever wrote them (the page, an import, the assistant, a hand-edited file):
// a member is a link, a webtop link or a group that exists, every tile is in one group at most (the first group in
// the list that names it) and no group is inside itself, however far down (groups nest as deep as they like). A
// reference that breaks a rule is dropped, so that tile shows one level up or on the webtop instead. Groups left
// empty are kept (the page deletes them).
export function tidyGroups(shortcuts) {
  const byId = new Map(shortcuts.map((t) => [t.id, t]));
  const groups = shortcuts.filter((t) => t.kind === 'group' && Array.isArray(t.data?.items));
  const parent = new Map();
  for (const g of groups) {
    g.data.items = g.data.items.filter((id) => {
      const m = byId.get(id);
      if (!m || m === g || !GROUPABLE.includes(m.kind) || parent.has(id)) return false;
      parent.set(id, g);
      return true;
    });
  }
  const drop = (child) => {
    const p = parent.get(child.id);
    p.data.items = p.data.items.filter((id) => id !== child.id);
    parent.delete(child.id);
  };
  for (const g of groups) {
    // Walking up from g: meeting g again is a loop, cut where it closes.
    const seen = new Set([g]);
    for (let p = parent.get(g.id); p; p = parent.get(p.id)) {
      if (seen.has(p)) { drop(g); break; }
      seen.add(p);
    }
  }
  return shortcuts;
}

// The tiles left after deleting `ids`: a deleted group takes everything inside it, however far down, and a group
// the deletion leaves empty goes too (then the group around it, if that is left empty in turn). The references to
// deleted tiles are dropped by tidyGroups when the webtop is saved.
export function removeTiles(shortcuts, ids) {
  const byId = new Map(shortcuts.map((t) => [t.id, t]));
  const gone = new Set();
  const todo = ids.filter((id) => byId.has(id));
  while (todo.length) {
    const id = todo.pop();
    if (gone.has(id)) continue;
    gone.add(id);
    const t = byId.get(id);
    if (t.kind === 'group') todo.push(...(t.data?.items ?? []).filter((m) => byId.has(m)));
  }
  for (let emptied = true; emptied;) {
    emptied = false;
    for (const g of shortcuts) {
      const items = g.kind === 'group' && !gone.has(g.id) ? g.data?.items ?? [] : [];
      if (items.length && items.every((m) => gone.has(m))) { gone.add(g.id); emptied = true; }
    }
  }
  return shortcuts.filter((t) => !gone.has(t.id));
}
// A time zone this runtime knows (the page uses the same IANA names), else '' (the viewer's own).
function validZone(z) {
  const zone = String(z ?? '').trim().slice(0, 64);
  if (!zone) return '';
  try { new Intl.DateTimeFormat('en', { timeZone: zone }); return zone; } catch { return ''; }
}
const TILE_COLORS = ['#ffffff', '#000000']; // a clock's or countdown's data.color; client.js has the same (TILE_BACKGROUNDS)
const COUNTDOWN_MAX_S = 200 * 365 * 86400; // the furthest end a countdown takes, in seconds from now
// When a tile was added, in ms (Sort By Date Added): kept from the input, else now. Tiles from before it was kept
// have 0 (migrate), so they count as the oldest.
const addedAt = (v) => (Number.isInteger(v) && v >= 0 && v <= Date.now() ? v : Date.now());
export function newShortcut(input) {
  if (Object.hasOwn(APPLET_KINDS, input?.kind ?? '')) input = { ...input, ...appletTile(input) };
  const kind = KINDS.includes(input.kind) ? input.kind : 'url';
  const size = isSize(input.size) ? input.size : SIZES[kind][0];
  const data = isData(input.data) && dataFits(input.data) ? { ...input.data } : {};
  const layouts = cleanLayouts(input.layouts, kind);
  const base = { id: uuid(), kind, size, x: coord(input.x, 40), y: coord(input.y, 40), added: addedAt(input.added), data, ...(layouts && { layouts }) };
  if (kind === 'note') {
    // Exports from before `data` carry the text on its own.
    data.text = String(data.text ?? input.text ?? '').slice(0, 5000);
    return {
      ...base,
      title: String(input.title ?? 'Note').slice(0, 80),
      color: HEX.test(input.color ?? '') ? input.color.toLowerCase() : null, // null: the theme's note colour
    };
  }
  if (kind === 'group') {
    // A group holds references to other tiles: data.items lists their ids, in order: links, webtop links and other
    // groups, never widgets (tidyGroups checks the whole tree when the webtop is saved). Its title may be null.
    const ids = Array.isArray(data.items) ? data.items : [];
    data.items = [...new Set(ids.filter((id) => typeof id === 'string' && id.length <= 64))].slice(0, GROUP_MAX);
    const title = input.title == null ? '' : String(input.title).trim().slice(0, 80);
    return { ...base, title: title || null };
  }
  if (kind === 'slideshow') {
    // data.images: what it shows, in order (slideImage; duplicates kept, as the owner may want one twice).
    // data.interval: seconds per image (SLIDE_INTERVALS, 5 by default); data.effect: 'fade' or 'slide' between them;
    // data.fit: 'cover' (fills the tile, cropped) or 'contain' (the whole image, letterboxed). One image stays still.
    data.images = (Array.isArray(data.images) ? data.images : []).filter(slideImage).slice(0, SLIDES_MAX);
    const interval = Number(data.interval);
    data.interval = SLIDE_INTERVALS.includes(interval) ? interval : 5;
    data.effect = SLIDE_EFFECTS.includes(data.effect) ? data.effect : SLIDE_EFFECTS[0];
    data.fit = SLIDE_FITS.includes(data.fit) ? data.fit : SLIDE_FITS[0];
    return { ...base, title: String(input.title ?? 'Slideshow').slice(0, 80) };
  }
  if (kind === 'applet') {
    // data.applet and data.widget name the applet's widget this tile shows (the rest of data is the widget's own
    // state, through its setData); a name that is not one is emptied, and the tile says so.
    data.applet = APPLET_ID_RE.test(data.applet ?? '') ? data.applet : '';
    data.widget = APPLET_NAME_RE.test(data.widget ?? '') ? data.widget : '';
    return { ...base, title: String(input.title ?? '').slice(0, 80) };
  }
  if (kind === 'clock') {
    // data.face: 'digital' or 'analog'. data.seconds, data.h24 (24-hour time) and data.date (show the date): on / off.
    // data.zone: an IANA time zone ('Asia/Tokyo'), or '' for the viewer's own; data.label: an optional caption.
    data.face = data.face === 'analog' ? 'analog' : 'digital';
    data.seconds = data.seconds === true;
    data.h24 = data.h24 === true;
    data.date = data.date !== false;
    data.zone = validZone(data.zone);
    data.label = String(data.label ?? '').trim().slice(0, 40);
    // data.color: white or black (TILE_COLORS) behind the figures or on the dial; missing means transparent
    const color = String(data.color ?? '').toLowerCase();
    if (TILE_COLORS.includes(color)) data.color = color; else delete data.color;
    return { ...base, title: String(input.title ?? '').slice(0, 80) };
  }
  if (kind === 'countdown') {
    // data.target: when it ends, ms since 1970 (a number, or anything Date.parse reads, which the assistant may give);
    // data.duration instead sets it to that many seconds from now. data.start: when it was set, which with the end
    // decides how many groups it shows (the client keeps it, and resets it when an ended countdown gets more time).
    // A countdown without a valid end is a five-minute one from now. data.label: an optional caption.
    const now = Date.now();
    const duration = Number(data.duration);
    let target = Number.isFinite(duration) && duration > 0 ? now + Math.min(duration, COUNTDOWN_MAX_S) * 1000
      : typeof data.target === 'string' ? Date.parse(data.target) : Number(data.target);
    if (!Number.isFinite(target) || target <= 0) target = now + 5 * 60 * 1000;
    target = Math.round(Math.min(target, now + COUNTDOWN_MAX_S * 1000));
    const start = Number(data.start);
    data.start = Number.isFinite(start) && start > 0 && start <= target && !(duration > 0) ? Math.round(start) : Math.min(now, target);
    data.target = target;
    delete data.duration;
    data.label = String(data.label ?? '').trim().slice(0, 40);
    const color = String(data.color ?? '').toLowerCase();
    if (TILE_COLORS.includes(color)) data.color = color; else delete data.color;
    return { ...base, title: String(input.title ?? '').slice(0, 80) };
  }
  const target = String(input.target ?? '').slice(0, 2000);
  return {
    ...base,
    title: String(input.title ?? 'Untitled').slice(0, 80),
    icon: String(input.icon ?? '').slice(0, 500),   // emoji or image URL; empty: the site's own icon, found when drawn
    // URL, a webtop slug, or an applet's command (an id that is not one is emptied, and the tile asks for an address)
    target: kind === 'command' && !COMMAND_TARGET_RE.test(target) ? '' : target,
  };
}
